// M-97 (promo): 汎用アンケートの**形式変換と確度導出の真実源**（純関数 / SDK 非依存）。
//
// M-78〜M-79 のアンケートは「1 設問・4 択固定（+ 車検満了日）」で、4 択は**確度昇格エンジンの
// 入力そのもの**だった。M-97 で設問を配列化し任意の選択肢を許すにあたり、既存の配線
// （`stageGrantedBySurveyChoice` / `isInterestOptedOut` / HOT 起票）を**一切変えない**ために:
//
//   1. 確度は**選択肢の属性**（`PromoSurveyOption.interestChoice`）として持たせる
//   2. 旧形式の doc は **backfill せず読み取り時に変換**する（`normalizePromoSurvey`）。
//      旧 4 択は各自のキーが `interestChoice` に入るため、変換後も確度の挙動が完全に一致する
//   3. 回答は `answers`（設問 id → 値）に持ち、確度用の `choice` はそこから**導出**する
//
// なぜ shared に置くか: functions（保存・確度反映）と tenant-promo（編集 UI・プレビュー）と
// customer（回答画面）の 3 者が同じ規則で読む必要があるため。書き写すと必ず drift する。
import { SURVEY_CHOICE_KEYS, } from './types.js';
import { DEFAULT_INSPECTION_DUE_LABEL, DEFAULT_SURVEY_CHOICE_LABELS, isValidSurveyOptionId, } from './promoTypes.js';
import { LINE_QUICK_REPLY_MAX_ITEMS, resolveSurveyChoiceLabel, } from './promoSurveyQuickReply.js';
/**
 * 旧形式（1 設問・4 択）から変換したときの設問 id。
 *
 * **固定値であることが重要** — 回答は設問 id をキーに保存されるため、変換のたびに変わると
 * 過去の回答と突き合わせられなくなる。旧 doc を新形式で保存し直す場合もこの id を使う。
 */
export const LEGACY_CHOICE_QUESTION_ID = 'legacy_choice';
/** 旧形式の車検満了日設問（M-79）から変換したときの設問 id。 */
export const LEGACY_INSPECTION_DUE_QUESTION_ID = 'legacy_inspection_due';
/**
 * 確度紐付けの**強さ順**（大きいほど関心が強い）。
 *
 * `SURVEY_CHOICE_KEYS` の配列順と一致するが、**暗黙の index 依存にしない**ために明示する
 * （閉集合に要素が足された / 並び替えられたときに、確度の優劣が無言で変わるのを防ぐ）。
 *
 * `not_interested` を 0（最弱）に置くのは意図的: 「オイル交換に興味」と「特にない」が同時に
 * 選ばれた場合、**関心ありを優先**する。`not_interested` は配信抑制（嫌われ防止）に効くため、
 * 関心の表明と同時に立てると顧客の意思に反して配信が止まる。
 */
export const INTEREST_CHOICE_STRENGTH = {
    not_interested: 0,
    slightly_interested: 1,
    interested_undecided: 2,
    interested_planned: 3,
};
/** 設問数の上限（編集 UI・保存 zod の共通根拠）。 */
export const SURVEY_MAX_QUESTIONS = 20;
/** 1 設問あたりの選択肢数の上限。 */
export const SURVEY_MAX_OPTIONS = 20;
/** 自由記述の既定の最大文字数。 */
export const SURVEY_TEXT_DEFAULT_MAX_LENGTH = 500;
/**
 * 旧 4 択を新形式の選択肢へ展開する（純関数）。
 *
 * 各選択肢の `interestChoice` に**自分自身のキー**を入れるため、変換後も
 * `deriveInterestChoiceFromAnswers` が旧 `choice` と同じ値を返す。
 * 文言解決は `resolveSurveyChoiceLabel`（空文字は既定に倒す `||` 規則）を再利用する。
 */
export function buildLegacyChoiceOptions(choiceLabels) {
    return SURVEY_CHOICE_KEYS.map((key) => ({
        id: key,
        label: resolveSurveyChoiceLabel(key, choiceLabels),
        interestChoice: key,
    }));
}
/**
 * アンケート定義を新形式（`questions` を持つ）に正規化する（純関数）。
 *
 * **backfill しない設計の要**。旧 doc（`question` / `choiceLabels` / `askInspectionDue`）は
 * ここで questions[] に変換され、新 doc（`questions` あり）はそのまま通る。
 * 読む側は必ずこれを通すこと。
 *
 * 旧 → 新の対応:
 *   - `question` → `choice` 設問 1 つ（id = `legacy_choice` / 4 択・単一選択・必須）
 *   - `askInspectionDue === true` → `month` 設問（id = `legacy_inspection_due` /
 *     `reflectToInspectionDue: true` = `users.vehicles` へ反映する M-79 の挙動を保つ / 任意）
 */
export function normalizePromoSurvey(survey) {
    if (survey.questions && survey.questions.length > 0) {
        return survey;
    }
    const questions = [
        {
            id: LEGACY_CHOICE_QUESTION_ID,
            type: 'choice',
            text: survey.question,
            options: buildLegacyChoiceOptions(survey.choiceLabels),
            required: true,
        },
    ];
    if (survey.askInspectionDue === true) {
        questions.push({
            id: LEGACY_INSPECTION_DUE_QUESTION_ID,
            type: 'month',
            text: survey.inspectionDueLabel?.trim() || DEFAULT_INSPECTION_DUE_LABEL,
            reflectToInspectionDue: true,
        });
    }
    return { ...survey, questions };
}
/**
 * 回答 doc を新形式（`answers` を持つ）に正規化する（純関数）。
 *
 * 旧 doc は `choice` と `inspectionDueMonth` しか持たないため、`normalizePromoSurvey` が
 * 作るのと同じ設問 id をキーにして `answers` を組み立てる。
 */
export function normalizePromoSurveyResponse(response) {
    if (response.answers && Object.keys(response.answers).length > 0) {
        return response;
    }
    const answers = {};
    if (response.choice) {
        answers[LEGACY_CHOICE_QUESTION_ID] = { type: 'choice', optionIds: [response.choice] };
    }
    if (response.inspectionDueMonth) {
        answers[LEGACY_INSPECTION_DUE_QUESTION_ID] = {
            type: 'month',
            value: response.inspectionDueMonth,
        };
    }
    return { ...response, answers };
}
/**
 * 回答から確度エンジンへ渡す `choice` を導出する（純関数 / M-97 の中核）。
 *
 * 選ばれた選択肢のうち `interestChoice` を持つものを集め、**最も強いもの**
 * （`INTEREST_CHOICE_STRENGTH`）を返す。紐付けが 1 つも無ければ `undefined` =
 * **確度エンジンに一切触れない**（集計・セグメント抽出にだけ使われる汎用設問）。
 *
 * 定義に存在しない選択肢 id（設問を編集した後に古いバンドルから届いた回答など）は無視する。
 */
export function deriveInterestChoiceFromAnswers(survey, answers) {
    const normalized = normalizePromoSurvey(survey);
    let best;
    for (const question of normalized.questions) {
        if (question.type !== 'choice')
            continue;
        const answer = answers[question.id];
        if (!answer || answer.type !== 'choice')
            continue;
        for (const optionId of answer.optionIds) {
            const option = question.options.find((o) => o.id === optionId);
            const choice = option?.interestChoice;
            if (!choice)
                continue;
            if (best === undefined ||
                INTEREST_CHOICE_STRENGTH[choice] > INTEREST_CHOICE_STRENGTH[best]) {
                best = choice;
            }
        }
    }
    return best;
}
/**
 * `reflectToInspectionDue` が立った month 設問の回答（= 車検満了年月）を取り出す（純関数）。
 *
 * M-79 の `users.vehicles[].inspectionDueDate` 反映は**この設問の回答だけ**が駆動する。
 * 一般の month 設問（「次回来店の予定時期」など）を反映すると、車検期限として配信を
 * 駆動してしまい事故になる。
 */
export function extractInspectionDueMonth(survey, answers) {
    const normalized = normalizePromoSurvey(survey);
    for (const question of normalized.questions) {
        if (question.type !== 'month' || question.reflectToInspectionDue !== true)
            continue;
        const answer = answers[question.id];
        if (answer?.type === 'month' && answer.value)
            return answer.value;
    }
    return undefined;
}
/**
 * **1 タップで答えられる設問**（choice ちょうど 1 問・単一選択）を取り出す中核（純関数）。
 *
 * クイックリプライ（M-95 / トーク版）とアンケートカードの選択肢ボタン（M-110）は
 * **同じ postback 形式**を使うので、送れるかの条件も同じ — 唯一違うのは
 * **選択肢の件数上限**だけ（クイックリプライは LINE 仕様で 13 / Flex のボタンには
 * その制限が無い）。規則を 2 本に分けると必ず片方だけ直って食い違うので、
 * 上限を引数で受けてここ 1 本に閉じる（下のラッパ 2 つが上限を与える）。
 *
 * トーク版で送れるかの判定は**この 1 本だけ**（M-106）。以前は編集画面の警告用と
 * 送信ガード用の 2 系統があり、**両方向に誤っていた**:
 *   - 編集画面側（旧 `isTalkDeliverableQuestions`）は設問数だけを見て選択肢を見ず、
 *     配信側で弾かれる構成を**無警告で作れてしまった**
 *   - 送信側（旧 `getQuickReplyDeliverableQuestion`）は最初の choice 設問だけを見て
 *     設問数を見ず、「4 択 + 車検満了日」を編集画面が**送れないと誤警告**していた
 * 規則を 2 箇所に持つ限りこの手の食い違いは再発するので、doc 版・配列版とも
 * ここへ委譲する（doc 版は `normalizePromoSurvey` を通すだけの差）。
 *
 * 送れる条件:
 *   1. **選択式（choice）の設問がちょうど 1 つ**。0 個は送るものが無い。2 個以上を許すと、
 *      postback（`c=<選択肢 id>`）から**どの設問への回答か一意に決まらない** —
 *      設問をまたいで同じ選択肢 id を使えるうえ、回答は `answers[設問 id]` に保存するため、
 *      取り違えると別設問の集計に混ざる。choice 以外（自由記述・年月・数値）の設問が
 *      同居するのは可（トークでは聞けないので `getQuickReplyOmittedQuestions...` が列挙し、
 *      UI が理由を明示する / M-95 の「4 択 + 車検満了日」運用）
 *   2. **単一選択**（クイックリプライは 1 タップで確定するため複数選択を表現できない）
 *   3. 選択肢が **1〜`maxOptions` 個**。先頭 N 個へ黙って切らず**送れないと判定する**
 *      （切ると、切られた選択肢の回答がゼロ件になるだけで、集計を見ても異常だと
 *      分からない / fail-closed）
 *   4. 全選択肢の id が postback に載る形式（`isValidSurveyOptionId` = 保存側 zod と同値）。
 *      保存経路を通った id は必ず満たすので、これが効くのは Admin SDK 直書きなどの
 *      異常データだけ — それでも**送れてしまうと回答が定義照合で消える**ので弾く
 */
function pickSingleTapChoiceQuestion(questions, maxOptions) {
    const choiceQuestions = questions.filter((q) => q.type === 'choice');
    if (choiceQuestions.length !== 1)
        return null;
    const question = choiceQuestions[0];
    if (question.multiple === true)
        return null;
    if (question.options.length === 0)
        return null;
    if (question.options.length > maxOptions)
        return null;
    // 前後に空白を持つ id は「送れない」に倒す（fail-closed）。`isValidSurveyOptionId` は
    // トリム後の長さで判定するので `' o_oil '` を通してしまうが、`buildSurveyQuickReplyItems` は
    // id をトリムせず postback に載せる一方、`parseSurveyPostbackData` は保存側 zod（`.trim()`）
    // と揃えてトリム後の値を返す。結果 **タップしても定義照合で回答が 0 件になる**（無言の取りこぼし）。
    // 保存経路を通った id は zod が必ずトリム済みなので通常は到達しないが、この条件の目的自体が
    // 「Admin SDK 直書きなどの異常データで『送れたのに回答が消える』を防ぐ」ことなので、
    // ここで落とさないと条件の主張（送れる ⟺ postback が往復する）が成立しない。
    if (!question.options.every((o) => o.id === o.id.trim() && isValidSurveyOptionId(o.id))) {
        return null;
    }
    return question;
}
/**
 * **クイックリプライ**（トーク上のボタン列 / M-95・M-106）で送る設問（純関数 / 設問配列版）。
 *
 * 上限は LINE 仕様の `LINE_QUICK_REPLY_MAX_ITEMS`（13）。定義側の上限
 * （`SURVEY_MAX_OPTIONS` = 20）は据え置きなので、14〜20 択の定義は正当に存在しうる
 * — それは**クイックリプライでは送れない**が、カード版（下）では送れる。
 */
export function getQuickReplyDeliverableQuestionFromQuestions(questions) {
    return pickSingleTapChoiceQuestion(questions, LINE_QUICK_REPLY_MAX_ITEMS);
}
/**
 * **アンケートカードの選択肢ボタン**（M-110）で送る設問（純関数 / 無ければ null）。
 *
 * クイックリプライとの違いは件数上限だけ。Flex のボタンには 13 個制限が無く、
 * bubble の JSON 上限（30 KB / LINE 仕様）に対して 20 択でも数 KB なので、
 * **上限は定義側（`SURVEY_MAX_OPTIONS`）に一致させる** = 「保存できる定義はすべて送れる」。
 * ここに独自のより小さい上限を置くと、**管理画面では作れるのにカードでは送れない**
 * 定義が生まれ、その差を運用者が説明できない。
 *
 * null のときは従来どおり「答える」ボタン（回答ページ = LIFF）に倒す
 * （複数の choice 設問 / 複数選択 / 選択肢 0 個 / 21 択以上 / id が postback に載らない形）。
 */
export function getChoiceCardDeliverableQuestion(survey) {
    return pickSingleTapChoiceQuestion(normalizePromoSurvey(survey).questions, SURVEY_MAX_OPTIONS);
}
/**
 * クイックリプライで送る設問を取り出す（純関数 / 無ければ null / `PromoSurvey` doc 版）。
 *
 * 旧 doc は `normalizePromoSurvey` で新形式に変換してから判定するため、
 * **旧 4 択のアンケートは従来どおり送れる**（4 択キーがそのまま選択肢 id になる）。
 */
export function getQuickReplyDeliverableQuestion(survey) {
    return getQuickReplyDeliverableQuestionFromQuestions(normalizePromoSurvey(survey).questions);
}
/** クイックリプライで送れるアンケートか（`getQuickReplyDeliverableQuestion` の真偽値版）。 */
export function isQuickReplyDeliverableSurvey(survey) {
    return getQuickReplyDeliverableQuestion(survey) !== null;
}
/**
 * クイックリプライで**送れない設問**（トークでは聞けない設問）の一覧（純関数 / 設問配列版）。
 *
 * 選択式そのものは送れても、車検満了日（年・月の 2 段階選択）や自由記述はトークの
 * ボタン列では表現できない。**黙って省略すると「設定したのに集まらない」が無言で起きる**
 * ため、UI がこれを使って理由を出す（M-95 の車検満了日の警告を一般化したもの）。
 *
 * 送れないアンケートでは空配列を返す（`not-talk-deliverable` の警告と二重にならないよう、
 * 「送れる。ただし一部は聞けない」ときだけ列挙する）。
 */
export function getQuickReplyOmittedQuestionsFromQuestions(questions) {
    const deliverable = getQuickReplyDeliverableQuestionFromQuestions(questions);
    if (!deliverable)
        return [];
    return questions.filter((q) => q.id !== deliverable.id);
}
/** クイックリプライで送れない設問の一覧（`PromoSurvey` doc 版）。 */
export function getQuickReplyOmittedQuestions(survey) {
    return getQuickReplyOmittedQuestionsFromQuestions(normalizePromoSurvey(survey).questions);
}
/**
 * 設問定義の妥当性を検証する（純関数 / 編集 UI と保存 zod の共通根拠）。
 *
 * **id の一意性**を最優先で見る — 重複すると `answers` が後勝ちで上書きされ、
 * 回答が無言で消える（保存はできてしまうので気付けない）。
 */
export function validatePromoSurveyQuestions(questions) {
    const errors = [];
    if (questions.length === 0) {
        errors.push({ message: '設問を 1 つ以上追加してください' });
    }
    if (questions.length > SURVEY_MAX_QUESTIONS) {
        errors.push({ message: `設問は ${SURVEY_MAX_QUESTIONS} 個までです` });
    }
    const seenQuestionIds = new Set();
    for (const question of questions) {
        if (!question.id.trim()) {
            errors.push({ message: '設問 ID が空です' });
        }
        else if (seenQuestionIds.has(question.id)) {
            errors.push({
                questionId: question.id,
                message: `設問 ID が重複しています（${question.id}）`,
            });
        }
        seenQuestionIds.add(question.id);
        if (!question.text.trim()) {
            errors.push({ questionId: question.id, message: '設問文を入力してください' });
        }
        // 数値設問の min > max は**顧客が回答不能な設問**になる（どの値を入れても範囲外）。
        // 保存できてしまうと、配信してから «誰も答えられない» ことに気付く形になる。
        if (question.type === 'number' &&
            question.min !== undefined &&
            question.max !== undefined &&
            question.min > question.max) {
            errors.push({
                questionId: question.id,
                message: '最小値は最大値以下にしてください',
            });
        }
        if (question.type !== 'choice')
            continue;
        if (question.options.length === 0) {
            errors.push({
                questionId: question.id,
                message: '選択肢を 1 つ以上追加してください',
            });
        }
        if (question.options.length > SURVEY_MAX_OPTIONS) {
            errors.push({
                questionId: question.id,
                message: `選択肢は ${SURVEY_MAX_OPTIONS} 個までです`,
            });
        }
        const seenOptionIds = new Set();
        for (const option of question.options) {
            if (!option.id.trim()) {
                errors.push({ questionId: question.id, message: '選択肢 ID が空です' });
            }
            else if (seenOptionIds.has(option.id)) {
                errors.push({
                    questionId: question.id,
                    message: `選択肢 ID が重複しています（${option.id}）`,
                });
            }
            seenOptionIds.add(option.id);
            if (!option.label.trim()) {
                errors.push({
                    questionId: question.id,
                    message: '選択肢の文言を入力してください',
                });
            }
        }
    }
    return errors;
}
/**
 * 設問配列から**旧形式フィールドを再構成する**（純関数 / M-97 PR5）。
 *
 * なぜ要るか: `applySurveyUpsert` は `tx.set` の完全置換で保存するため、編集 UI が
 * `questions` だけを送ると旧フィールドが**消える**。旧フィールドはまだ現役の読み手が居る:
 *
 *   - `SurveysListPage` / `PlanSurveySection` の一覧行（`question`）
 *   - `SurveyResponseSummary` の 4 択分布ラベル（`choiceLabels` / `askInspectionDue`）
 *   - トーク版クイックリプライ（`SegmentComposePage` → `sendSegmentPush` の `choiceLabels`）
 *
 * 消えると「稼働中のアンケートを編集画面で開いて保存しただけで、トーク版の選択肢文言が
 * 既定に戻る」といった無言の劣化が起きる。読み手を `questions` ベースへ移すのは後続 PR
 * （集計 UI / トーク版ガード）なので、それまでは**保存のたびに旧フィールドを作り直す**。
 *
 * - `question`: 最初の選択式設問の設問文（無ければ先頭設問 / 設問なしは空文字）
 * - `choiceLabels`: 最初の選択式設問のうち**選択肢 id が確度キーそのもの**のもの
 *   （= 旧 4 択から変換された選択肢 / 新規追加の選択肢は旧形式で表現できないので含めない）
 * - `askInspectionDue` / `inspectionDueLabel`: `reflectToInspectionDue` の month 設問
 */
export function buildLegacyPromoSurveyFields(questions) {
    const firstChoice = questions.find((q) => q.type === 'choice');
    const question = (firstChoice ?? questions[0])?.text.trim() ?? '';
    const choiceLabels = {};
    for (const option of firstChoice?.options ?? []) {
        if (!SURVEY_CHOICE_KEYS.includes(option.id))
            continue;
        const key = option.id;
        const label = option.label.trim();
        // 既定と同じ文言は焼き込まない（旧編集 UI の「空欄は送らない」仕様と揃える）。
        if (!label || label === DEFAULT_SURVEY_CHOICE_LABELS[key])
            continue;
        choiceLabels[key] = label;
    }
    const inspectionDue = questions.find((q) => q.type === 'month' && q.reflectToInspectionDue === true);
    const inspectionDueLabel = inspectionDue?.text.trim();
    return {
        question,
        ...(Object.keys(choiceLabels).length > 0 ? { choiceLabels } : {}),
        askInspectionDue: inspectionDue !== undefined,
        ...(inspectionDueLabel && inspectionDueLabel !== DEFAULT_INSPECTION_DUE_LABEL
            ? { inspectionDueLabel }
            : {}),
    };
}
/**
 * 設問定義の**誤設定になりやすい点**を洗い出す（純関数 / 編集 UI と配信作成 UI の共通根拠）。
 *
 * `validatePromoSurveyQuestions` が返すのは保存を止めるエラー。こちらは
 * **保存してよいが黙って期待外れになる**設定で、UI が理由を明示するために使う
 * （黙って一部だけ効くと「設定したのに集まらない」が無言で起きる / M-95 の教訓）。
 */
export function collectPromoSurveyDesignWarnings(questions) {
    const warnings = [];
    if (questions.length === 0)
        return warnings;
    // M-106: 送信ガード（`sendSegmentPush`）と**同じ 1 本**で判定する。
    // 別の規則で警告を出していた頃は「編集画面は無警告なのに配信画面でボタンが押せない」
    // 「送れるのに送れないと警告する」が両方起きていた。
    const deliverable = getQuickReplyDeliverableQuestionFromQuestions(questions);
    if (!deliverable) {
        warnings.push({
            code: 'not-talk-deliverable',
            message: `この構成はトーク画面のクイックリプライでは送れません（送れるのは「選択式の設問がちょうど 1 つ・単一選択・選択肢 ${LINE_QUICK_REPLY_MAX_ITEMS} 個まで」の構成）。回答ページへのリンクでのみ配信できます。`,
        });
    }
    // 送れる構成でも、選択式以外の設問（自由記述・年月・数値）はトークでは聞けない。
    // 判定を「choice がちょうど 1 つ」に緩めた結果、以前は `not-talk-deliverable` が
    // 拾っていた「4 択 + 自由記述」が deliverable 側へ移った。ここで拾わないと
    // **警告ゼロで「設定したのに集まらない」が復活する**（M-95 の教訓に正面から反する）。
    for (const omitted of getQuickReplyOmittedQuestionsFromQuestions(questions)) {
        warnings.push({
            code: 'quick-reply-omits-questions',
            questionId: omitted.id,
            message: `「${omitted.text.trim()}」はトーク画面では聞けません（クイックリプライで送れるのは選択式の 1 設問だけ）。この設問も聞くなら、回答ページへのリンクで配信してください。`,
        });
    }
    const hasInterestBinding = questions.some((q) => q.type === 'choice' && q.options.some((o) => o.interestChoice));
    if (!hasInterestBinding) {
        warnings.push({
            code: 'no-interest-binding',
            message: '確度を紐づけた選択肢がありません。回答は集計とセグメント抽出には使えますが、興味ステージ（確度）は動きません。',
        });
    }
    const inspectionDueQuestions = questions.filter((q) => q.type === 'month' && q.reflectToInspectionDue === true);
    if (inspectionDueQuestions.length > 1) {
        warnings.push({
            code: 'duplicate-inspection-due',
            questionId: inspectionDueQuestions[1]?.id,
            message: '車検満了日として反映する設問が複数あります。顧客情報に反映されるのは最初の 1 つだけです。',
        });
    }
    for (const question of questions) {
        if (question.type !== 'choice' || question.multiple !== true)
            continue;
        if (!question.options.some((o) => o.interestChoice === 'not_interested'))
            continue;
        warnings.push({
            code: 'not-interested-with-multiple',
            questionId: question.id,
            message: '複数選択の設問に「関心なし」を紐づけた選択肢があります。関心ありの選択肢と同時に選ばれた場合は、関心ありが優先されます（配信は止まりません）。',
        });
    }
    return warnings;
}
/**
 * 回答を**定義に照らして sanitize する**（純関数 / 保存前に必ず通す）。
 *
 * 回答は顧客の端末から来る（LIFF Callable / トーク版 postback）ため、定義に無い設問 id・
 * 設問型と食い違う値・存在しない選択肢 id が**実際に到達しうる**。素通しすると
 * `promo_survey_responses` と users ミラーにゴミが溜まり、読み手（集計 UI・セグメント抽出）が
 * 定義と突き合わせられない値を掴む。ここで定義にあるものだけに絞る。
 *
 * - 定義に無い設問 id は捨てる
 * - 設問型と `value.type` が食い違うものは捨てる
 * - `choice` は定義に存在する選択肢 id のみ残す（1 つも残らなければ設問ごと捨てる）
 * - 単一選択の設問に複数 id が来たら**先頭 1 つだけ**採る（詐称・古いバンドル対策）
 * - `null` は「提示したが未回答 / 取り下げ」として `clearedQuestionIds` に分ける
 */
export function pickAnswersDefinedInSurvey(survey, answers) {
    const normalized = normalizePromoSurvey(survey);
    const picked = {};
    const clearedQuestionIds = [];
    for (const question of normalized.questions) {
        if (!(question.id in answers))
            continue;
        const value = answers[question.id];
        if (value === null || value === undefined) {
            clearedQuestionIds.push(question.id);
            continue;
        }
        if (value.type !== question.type)
            continue;
        if (question.type === 'choice' && value.type === 'choice') {
            const valid = value.optionIds.filter((id) => question.options.some((o) => o.id === id));
            if (valid.length === 0)
                continue;
            picked[question.id] = {
                type: 'choice',
                optionIds: question.multiple === true ? valid : [valid[0]],
            };
            continue;
        }
        picked[question.id] = value;
    }
    return { answers: picked, clearedQuestionIds };
}
// ----------------------------------------------------------------------------
// users ミラー（セグメント抽出用）
// ----------------------------------------------------------------------------
/**
 * `AppUser.surveyAnswers` に保持するアンケート数の上限。
 *
 * 無制限に積むと users doc が Firestore の 1MB 上限へ近づき、配信対象 query
 * （`runSegmentPush` が users を全件読む単一経路）ごと重くなる。溢れた分の回答は
 * `promo_survey_responses` に残るので、失われるのは**抽出条件に使えること**だけ。
 */
export const SURVEY_ANSWER_MIRROR_LIMIT = 20;
/**
 * 回答から users ミラーに載せる分（**選択式の設問のみ**）を抜き出す（純関数）。
 *
 * 自由記述・数値・年月は載せない — users に PII を増やさないため、かつ
 * セグメント抽出の条件としても選択肢 id でなければ扱えないため。
 * 定義に存在しない選択肢 id（設問を編集した後に届いた古い回答）は落とす。
 */
export function extractChoiceAnswersForMirror(survey, answers) {
    const normalized = normalizePromoSurvey(survey);
    const mirror = {};
    for (const question of normalized.questions) {
        if (question.type !== 'choice')
            continue;
        const answer = answers[question.id];
        if (!answer || answer.type !== 'choice')
            continue;
        const valid = answer.optionIds.filter((id) => question.options.some((o) => o.id === id));
        if (valid.length > 0)
            mirror[question.id] = valid;
    }
    return mirror;
}
/**
 * users ミラーへ 1 アンケート分の回答を反映する（純関数 / 変更不要なら null）。
 *
 * `bumpLastSurveyAnswer`（`trackEvent.ts`）と同じ契約:
 *   - 不正な `answeredAt` は null（書かない）
 *   - **劣後する回答**（保存済みより古い / 同時刻）は null。遅着イベントで最新回答が
 *     巻き戻るのを防ぐ
 *
 * 上限を超えたら `answeredAt` の**古い順に落とす**。今回のアンケート自体は常に残す
 * （直近の回答が上限のせいで即座に消えると、回答直後の抽出で対象外になり説明できない）。
 */
export function bumpSurveyAnswerMirror(current, surveyId, entry) {
    if (!Number.isFinite(Date.parse(entry.answeredAt)))
        return null;
    if (Object.keys(entry.answers).length === 0)
        return null;
    const prev = current?.[surveyId];
    if (prev && !(Date.parse(entry.answeredAt) > Date.parse(prev.answeredAt)))
        return null;
    const next = {
        ...(current ?? {}),
        [surveyId]: entry,
    };
    const ids = Object.keys(next);
    if (ids.length <= SURVEY_ANSWER_MIRROR_LIMIT)
        return next;
    // 古い順に落とす。今回の surveyId は対象外（直近の回答を上限で消さない）。
    const droppable = ids
        .filter((id) => id !== surveyId)
        .sort((a, b) => Date.parse(next[a].answeredAt) - Date.parse(next[b].answeredAt));
    for (const id of droppable.slice(0, ids.length - SURVEY_ANSWER_MIRROR_LIMIT)) {
        delete next[id];
    }
    return next;
}
//# sourceMappingURL=promoSurveySchema.js.map